import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import Toast from '@/components/ui/Toast/toast';
import { createParsedEventHandler } from '@/hooks/socket/socket.helpers';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import { AppRoutes } from '@/i18n/constants';
import { useRouter } from '@/i18n/routing';
import openModal from '@/modals/utils/openModal';
import { useAppSelector } from '@/redux/hooks';
import { QueriesKeys } from '../queriesKeys';
import { tokenManager } from '../token-manager';
import {
  PlayerLevelUpContextSchema,
  PlayerQuestCompletedContextSchema,
  PlayerStatusUpContextSchema,
  PlayerXpEarnedContextSchema,
} from './player.schema';
import { applyPlayerXpEvent, clearPendingPlayerProgress } from './player.sync';

export const PLAYER_PROFILE_SOCKET_EVENTS = {
  levelUp: 'player.level-up',
  statusUp: 'player.status-up',
  xpEarned: 'player.xp-earned',
  questCompleted: 'quest.completed',
} as const;

const invalidatePlayerRewardState = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerGamificationRewards] });
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerGamificationGroupedRewards] });
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerProfile] });
};

const invalidateQuestCompletedState = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerQuests] });
  invalidatePlayerRewardState(queryClient);
};

const getMissionCompletedToastKey = (recurrenceType?: string | null) => {
  const normalizedType = recurrenceType?.trim().toLowerCase();

  switch (normalizedType) {
    case 'weekly':
      return 'weekly';
    case 'fast':
      return 'fast';
    case 'welcome':
    case 'once':
      return 'welcome';
    case 'daily':
    default:
      return 'daily';
  }
};

export function usePlayerSocket() {
  const userSession = useAppSelector(state => state.user.userSession);
  const isSessionActive = Boolean(userSession);
  const queryClient = useQueryClient();
  const t = useTranslations('gamification');
  const router = useRouter();

  useEffect(() => {
    if (!isSessionActive) {
      clearPendingPlayerProgress(queryClient);
    }
    return () => clearPendingPlayerProgress(queryClient);
  }, [isSessionActive, queryClient]);

  const handleLevelUp = createParsedEventHandler(
    PlayerLevelUpContextSchema,
    () => {
      invalidatePlayerRewardState(queryClient);
    },
  );

  const handleQuestCompleted = createParsedEventHandler(
    PlayerQuestCompletedContextSchema,
    (data) => {
      invalidateQuestCompletedState(queryClient);

      Toast({
        id: `mission-completed-${data.userQuestId}`,
        type: 'success',
        title: t(`mission-completed-toast.${getMissionCompletedToastKey(data.recurrenceType)}`),
        description: t('mission-completed-toast.description'),
        acceptButtonText: t('mission-completed-toast.view'),
        rejectButtonText: t('mission-completed-toast.later'),
        onAccept: () => router.push(AppRoutes.gamificationMissions),
        disableTimer: true,
      });
    },
  );

  const handleStatusUp = createParsedEventHandler(
    PlayerStatusUpContextSchema,
    (data) => {
      invalidatePlayerRewardState(queryClient);
      openModal('gm-congratulations-status-up', { statusId: data.currentStatusId });
    },
  );

  const handleXpEarned = createParsedEventHandler(
    PlayerXpEarnedContextSchema,
    (profileUpdate) => {
      applyPlayerXpEvent(queryClient, profileUpdate, tokenManager.getUserId());

      if (profileUpdate.source === 'streak') {
        // Open the streak XP earned toast when streak enhances the player's progress
        Toast({
          id: 'mission-completed-streak-xp-earned',
          type: 'success',
          title: t(`mission-completed-toast.streak-xp-earned`),
          acceptButtonText: t('mission-completed-toast.go-to-streak'),
          rejectButtonText: t('mission-completed-toast.later'),
          onAccept: () => router.push(AppRoutes.gamificationMissions),
        });
        queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerStreak] });
      }
    },
  );

  useSocketEvent(
    PLAYER_PROFILE_SOCKET_EVENTS.levelUp,
    handleLevelUp,
    isSessionActive,
  );

  // Status-up only opens the congratulations/claim entry point. The reward is
  // claimed later from the scratch-card flow after the user confirms the action.
  useSocketEvent(
    PLAYER_PROFILE_SOCKET_EVENTS.statusUp,
    handleStatusUp,
    isSessionActive,
  );

  // Listen for XP earned events and update progress in player bar
  useSocketEvent(
    PLAYER_PROFILE_SOCKET_EVENTS.xpEarned,
    handleXpEarned,
    isSessionActive,
  );

  useSocketEvent(
    PLAYER_PROFILE_SOCKET_EVENTS.questCompleted,
    handleQuestCompleted,
    isSessionActive,
  );
}
